import React, { useState, useEffect } from 'react';
import { blogAPI, getImageUrl } from '../../services/apiService';
import { Plus, Edit, Trash2, Eye, Filter } from 'lucide-react';

const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterPublished, setFilterPublished] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title_de: '',
    title_ru: '',
    excerpt_de: '',
    excerpt_ru: '',
    content_de: '',
    content_ru: '',
    author: 'FotografX',
    published: true,
    tags: []
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, [filterPublished]);

  const fetchPosts = async () => {
    try {
      const params = filterPublished !== 'all' ? { published: filterPublished === 'published' } : {};
      const response = await blogAPI.getAll(params);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreatePost = () => {
    setEditingPost(null);
    setFormData({
      title_de: '',
      title_ru: '',
      excerpt_de: '',
      excerpt_ru: '',
      content_de: '',
      content_ru: '',
      author: 'FotografX',
      published: true,
      tags: []
    });
    setSelectedImage(null);
    setShowForm(true);
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setFormData({
      title_de: post.title_de,
      title_ru: post.title_ru,
      excerpt_de: post.excerpt_de,
      excerpt_ru: post.excerpt_ru,
      content_de: post.content_de,
      content_ru: post.content_ru,
      author: post.author,
      published: post.published,
      tags: post.tags || []
    });
    setSelectedImage(null);
    setShowForm(true);
  };

  const handleDeletePost = async (postId) => {
    if (window.confirm('Are you sure you want to delete this blog post?')) {
      try {
        await blogAPI.delete(postId);
        await fetchPosts();
      } catch (error) {
        console.error('Error deleting post:', error);
        alert('Error deleting post');
      }
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      
      Object.keys(formData).forEach(key => {
        if (key === 'tags') {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });

      if (selectedImage) {
        formDataToSend.append('image', selectedImage);
      }

      if (editingPost) {
        await blogAPI.update(editingPost.id, formDataToSend);
      } else {
        await blogAPI.create(formDataToSend);
      }

      setShowForm(false);
      await fetchPosts();
    } catch (error) {
      console.error('Error saving post:', error);
      alert('Error saving post');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-300"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Управление блогом</h1>
          <p className="text-gray-300">Создание и управление статьями блога</p>
        </div>
        <button
          onClick={handleCreatePost}
          className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors duration-200"
        >
          <Plus className="h-5 w-5 mr-2" />
          Новая статья
        </button>
      </div>

      {/* Filters */}
      <div className="bg-gray-800 p-4 rounded-lg shadow-lg border border-gray-700">
        <div className="flex items-center space-x-4">
          <Filter className="h-5 w-5 text-gray-400" />
          <select
            value={filterPublished}
            onChange={(e) => setFilterPublished(e.target.value)}
            className="border border-gray-600 bg-gray-700 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">Все статьи</option>
            <option value="published">Только опубликованные</option>
            <option value="draft">Только черновики</option>
          </select>
          <div className="text-sm text-gray-300">
            {posts.length} {posts.length === 1 ? 'статья' : posts.length < 5 ? 'статьи' : 'статей'}
          </div>
        </div>
      </div>

      {/* Posts List */}
      <div className="bg-gray-800 shadow-lg overflow-hidden sm:rounded-md border border-gray-700">
        <ul className="divide-y divide-gray-700">
          {posts.map((post) => (
            <li key={post.id}>
              <div className="px-4 py-4 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0 h-16 w-16">
                    <img
                      className="h-16 w-16 rounded-md object-cover"
                      src={getImageUrl(post.thumbnail || post.image)}
                      alt={post.title_de}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-lg font-medium text-white truncate">
                        {post.title_de}
                      </p>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          post.published 
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {post.published ? 'Published' : 'Draft'}
                        </span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-300 truncate">
                      {post.excerpt_de}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      By {post.author} • {new Date(post.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleEditPost(post)}
                    className="p-2 text-gray-400 hover:text-indigo-400 transition-colors duration-200"
                    title="Редактировать"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => window.open(`/blog/${post.id}`, '_blank')}
                    className="p-2 text-gray-400 hover:text-green-400 transition-colors duration-200"
                    title="View"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeletePost(post.id)}
                    className="p-2 text-gray-400 hover:text-red-400 transition-colors duration-200"
                    title="Удалить"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {posts.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-300 text-lg">No blog posts found</p>
          <button
            onClick={handleCreatePost}
            className="mt-4 inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors duration-200"
          >
            <Plus className="h-5 w-5 mr-2" />
            Создать первую статью
          </button>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-600">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-white mb-6">
                {editingPost ? 'Редактировать статью' : 'Создать новую статью'}
              </h2>
              
              <form onSubmit={handleFormSubmit} className="space-y-6">
                {/* Featured Image */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Главное изображение *
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-indigo-600 file:text-white hover:file:bg-indigo-700"
                    required={!editingPost}
                  />
                  {selectedImage && (
                    <p className="text-sm text-gray-400 mt-1">
                      Выбрано: {selectedImage.name}
                    </p>
                  )}
                </div>

                {/* Titles */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Заголовок (немецкий) *
                    </label>
                    <input
                      type="text"
                      name="title_de"
                      value={formData.title_de}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Введите заголовок на немецком..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Заголовок (русский) *
                    </label>
                    <input
                      type="text"
                      name="title_ru"
                      value={formData.title_ru}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Введите название на русском..."
                    />
                  </div>
                </div>

                {/* Excerpts */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Excerpt (German) *
                    </label>
                    <textarea
                      name="excerpt_de"
                      value={formData.excerpt_de}
                      onChange={handleInputChange}
                      required
                      rows={3}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                      placeholder="Enter German excerpt..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Excerpt (Russian) *
                    </label>
                    <textarea
                      name="excerpt_ru"
                      value={formData.excerpt_ru}
                      onChange={handleInputChange}
                      required
                      rows={3}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                      placeholder="Введите краткое описание на русском..."
                    />
                  </div>
                </div>

                {/* Content */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Content (German) *
                    </label>
                    <textarea
                      name="content_de"
                      value={formData.content_de}
                      onChange={handleInputChange}
                      required
                      rows={8}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                      placeholder="Enter German content..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Content (Russian) *
                    </label>
                    <textarea
                      name="content_ru"
                      value={formData.content_ru}
                      onChange={handleInputChange}
                      required
                      rows={8}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                      placeholder="Введите содержание на русском..."
                    />
                  </div>
                </div>

                {/* Author and Published */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Author
                    </label>
                    <input
                      type="text"
                      name="author"
                      value={formData.author}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Author name..."
                    />
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="published"
                      checked={formData.published}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-600 bg-gray-700 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-300">
                      Publish immediately
                    </label>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-600">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-4 py-2 border border-gray-600 text-gray-300 rounded-md hover:bg-gray-700 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 transition-colors duration-200"
                  >
                    {isSubmitting ? 'Saving...' : (editingPost ? 'Update' : 'Create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBlog;